import React from 'react';
import RoutingUtil from '../util/RoutingUtil';
import trackedNavigate from './trackedNavigate';

/**
 * Determines if the click should open a new window based on modifier keys
 * @param {Event} e - The click event
 * @returns {boolean} - Whether to open a new window
 */
const shouldOpenWindow = (e) => e.ctrlKey || e.metaKey;

/**
 * Opens a new window with the given href
 * @param {string} href - The URL to open
 * @param {boolean} openBlankWindow - Whether to open in a new tab
 */
const callWindowOpen = (href, openBlankWindow = false) => {
  if (openBlankWindow) {
    window.open(href, '_blank');
  } else {
    window.open(href);
  }
};

/**
 * Handles click events for clickable components
 * @param {Event} e - The click event
 * @param {Object} props - The component props
 */
const handleClick = (e, props) => {
  const { onClick, href, target, forcedReload = false, openBlankWindow = false, state = {} } = props;

  e.preventDefault();

  if (onClick) {
    onClick(e);
    return;
  }

  if (!href) {
    return;
  }

  if (openBlankWindow || shouldOpenWindow(e) || target === '_blank') {
    const newhref = RoutingUtil.getPrefixedPath(href);
    callWindowOpen(newhref, e.shiftKey);
  } else {
    trackedNavigate(href, forcedReload, state);
  }
};

/**
 * Ensures href is prefixed with session path if present
 * @param {Object} props - The component props
 * @returns {Object} - New props with prefixed href
 */
const ensureHrefPrefixedIfPresent = (props) => {
  const newProps = { ...props };

  if (props.href) {
    newProps.href = RoutingUtil.getPrefixedPath(props.href);
  }

  return newProps;
};

/**
 * Custom hook for clickable DUI components that need href prefixing and click handling
 * @param {Object} props - The component props
 * @returns {Object} - { newProps, handleClickCallback }
 */
const useClickableProps = (props) => {
  const newProps = React.useMemo(() => ensureHrefPrefixedIfPresent(props), [props]);

  const handleClickCallback = React.useCallback((e) => {
    handleClick(e, newProps);
  }, [newProps]);

  return { newProps, handleClickCallback };
};

export default useClickableProps;
export { handleClick };
